import {useState, useMemo, useRef, useEffect, Fragment} from 'react';
import {Checkbox, Chip, Popover} from '@mui/material';
import {SVG} from '../../../../../../../../constants/assetLists';
import {ICONS} from '../../../../../../../users/data';
import {DepartmentFormValues, NotifyPlaceSelection, RoleFormValues} from '../../../../types';

export type SubBranchOption = {
  id: string;
  name: string;
  departments: DepartmentFormValues[];
  roles?: RoleFormValues[];
};

export type BranchOption = {
  id: string;
  name: string;
  departments: DepartmentFormValues[];
  roles?: RoleFormValues[];
  subBranches: SubBranchOption[];
};

export type ClientOption = {
  id: string;
  name: string;
  departments: DepartmentFormValues[];
  roles?: RoleFormValues[];
  branches: BranchOption[];
};

type PlaceEntry = {
  key: string;
  name: string;
  clientId: string;
  branchId?: string;
  subBranchId?: string;
  departments: DepartmentFormValues[];
};

function buildPlaceList(clients: ClientOption[]): PlaceEntry[] {
  const list: PlaceEntry[] = [];
  clients.forEach(client => {
    list.push({key: `${client.id}::`, name: client.name, clientId: client.id, departments: client.departments});
    client.branches.forEach(branch => {
      list.push({key: `${client.id}:${branch.id}:`, name: branch.name, clientId: client.id, branchId: branch.id, departments: branch.departments});
      branch.subBranches.forEach(sub => {
        list.push({key: `${client.id}:${branch.id}:${sub.id}`, name: sub.name, clientId: client.id, branchId: branch.id, subBranchId: sub.id, departments: sub.departments});
      });
    });
  });
  return list;
}

export function buildNotifyPlaceSelections(
  departmentId: string[] | 'all' | undefined,
  clients: ClientOption[],
): NotifyPlaceSelection[] {
  if (
    !departmentId ||
    (Array.isArray(departmentId) && departmentId.length === 0)
  ) {
    return [];
  }

  const isAll =
    departmentId === 'all' ||
    (Array.isArray(departmentId) && departmentId.includes('all'));
  const selectedDeptIds = isAll
    ? null
    : Array.isArray(departmentId)
      ? departmentId
      : [];

  const result: NotifyPlaceSelection[] = [];

  for (const p of buildPlaceList(clients)) {
    if (!p.departments.length) continue;

    if (isAll) {
      result.push({
        clientId: p.clientId,
        branchId: p.branchId,
        subBranchId: p.subBranchId,
        departmentIds: 'all',
      });
      continue;
    }

    const matched = p.departments
      .filter(d => selectedDeptIds!.includes(d.id))
      .map(d => d.id);
    if (!matched.length) continue;

    result.push({
      clientId: p.clientId,
      branchId: p.branchId,
      subBranchId: p.subBranchId,
      departmentIds: matched,
    });
  }

  return result;
}

export function getNotifChipLabel(sel: NotifyPlaceSelection, clients: ClientOption[]): string {
  const client = clients.find(c => c.id === sel.clientId);
  if (!client) return '';
  const branch = sel.branchId ? client.branches.find(b => b.id === sel.branchId) : undefined;
  const sub = sel.subBranchId && branch ? branch.subBranches.find(s => s.id === sel.subBranchId) : undefined;
  const placeName = sub?.name ?? branch?.name ?? client.name;
  if (sel.departmentIds === 'all') return `${placeName}: כל המחלקות`;
  if (Array.isArray(sel.departmentIds) && sel.departmentIds.length) {
    const allDepts = [
      ...client.departments,
      ...client.branches.flatMap(b => [...b.departments, ...b.subBranches.flatMap(s => s.departments)]),
    ];
    const names = sel.departmentIds.map(id => allDepts.find(d => d.id === id)?.name).filter(Boolean).join(', ');
    return `${placeName}: ${names}`;
  }
  return placeName;
}

// ─── Shared list UI — used inline in PersonalNotifPanel and inside the Popover
type NotifyDeptListProps = {
  clients: ClientOption[];
  value: NotifyPlaceSelection[];
  onChange: (val: NotifyPlaceSelection[]) => void;
  headerTextStyles?: string;
};

export function NotifyDeptList({
  clients,
  value,
  onChange,
  popoverRef,
  headerTextStyles,
}: NotifyDeptListProps & { popoverRef?: React.RefObject<{ updatePosition: () => void }> | null }) {
  const placeList = useMemo(() => buildPlaceList(clients), [clients]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(placeList.map((p) => p.key)),
  );

  useEffect(() => {
    setExpandedKeys(new Set(placeList.map((p) => p.key)));
  }, [placeList]);

  const pKey = (p: PlaceEntry) =>
    `${p.clientId}:${p.branchId ?? ""}:${p.subBranchId ?? ""}`;
  const getSel = (p: PlaceEntry) =>
    value.find(
      (v) =>
        v.clientId === p.clientId &&
        v.branchId === p.branchId &&
        v.subBranchId === p.subBranchId,
    );
  const withoutPlace = (p: PlaceEntry) =>
    value.filter(
      (v) =>
        `${v.clientId}:${v.branchId ?? ""}:${v.subBranchId ?? ""}` !== pKey(p),
    );

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (popoverRef?.current) {
      requestAnimationFrame(() => {
        popoverRef.current?.updatePosition();
      });
    }
  };

  const handlePlaceToggle = (p: PlaceEntry) => {
    const sel = getSel(p);
    const rest = withoutPlace(p);
    if (sel?.departmentIds === "all") onChange(rest);
    else
      onChange([
        ...rest,
        {
          clientId: p.clientId,
          branchId: p.branchId,
          subBranchId: p.subBranchId,
          departmentIds: "all",
        },
      ]);
  };

  const handleDeptToggle = (p: PlaceEntry, deptId: string | "all") => {
    const sel = getSel(p);
    const rest = withoutPlace(p);
    if (deptId === "all") {
      if (sel?.departmentIds === "all") onChange(rest);
      else
        onChange([
          ...rest,
          {
            clientId: p.clientId,
            branchId: p.branchId,
            subBranchId: p.subBranchId,
            departmentIds: "all",
          },
        ]);
      return;
    }
    const isAll = sel?.departmentIds === "all";
    const currentIds = Array.isArray(sel?.departmentIds)
      ? sel!.departmentIds
      : [];
    if (isAll) {
      const newIds = p.departments
        .map((d) => d.id)
        .filter((id) => id !== deptId);
      onChange([
        ...rest,
        {
          clientId: p.clientId,
          branchId: p.branchId,
          subBranchId: p.subBranchId,
          departmentIds: newIds.length ? newIds : [],
        },
      ]);
    } else if (currentIds.includes(deptId)) {
      const newIds = currentIds.filter((id) => id !== deptId);
      if (newIds.length)
        onChange([
          ...rest,
          {
            clientId: p.clientId,
            branchId: p.branchId,
            subBranchId: p.subBranchId,
            departmentIds: newIds,
          },
        ]);
      else onChange(rest);
    } else {
      const newIds = [...new Set([...currentIds, deptId])];
      if (
        p.departments.length === newIds.length &&
        p.departments.every((d) => newIds.includes(d.id))
      ) {
        onChange([
          ...rest,
          {
            clientId: p.clientId,
            branchId: p.branchId,
            subBranchId: p.subBranchId,
            departmentIds: 'all',
          },
        ]);
      } else {
        onChange([
          ...rest,
          {
            clientId: p.clientId,
            branchId: p.branchId,
            subBranchId: p.subBranchId,
            departmentIds: newIds,
          },
        ]);
      }
    }
  };

  const getCheckState = (p: PlaceEntry): "none" | "all" | "some" => {
    const sel = getSel(p);
    if (
      !sel ||
      (Array.isArray(sel.departmentIds) && sel.departmentIds.length === 0)
    )
      return "none";
    if (sel.departmentIds === "all") return "all";
    return "some";
  };

  return (
    <div>
      {placeList.map((p) => {
        if (!p.departments.length) return null;
        const sel = getSel(p);
        const checkState = getCheckState(p);
        const isExpanded = expandedKeys.has(p.key);
        return (
          <div dir="rtl" key={p.key} className="last:border-0">
            {/* Place header */}
            <div
              className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 select-none border-b border-gray80"
              onClick={() => toggleExpand(p.key)}
            >
              <div
                className="flex items-center gap-0.5"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={SVG.lessThanGrayIcon}
                  alt=""
                  className={`w-2 h-3 object-contain transition-transform duration-150 cursor-pointer ${isExpanded ? "-rotate-90" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(p.key);
                  }}
                />
                <Checkbox
                  size="small"
                  color="secondary"
                  sx={{ p: 0.5 }}
                  checked={checkState === "all"}
                  indeterminate={checkState === "some"}
                  onChange={() => handlePlaceToggle(p)}
                />
                <span className={`font-medium text-base ${headerTextStyles || ""}`}>{p.name}</span>
              </div>
            </div>

            {/* Department rows */}
            {isExpanded && (
              <div className="bg-white px-4">
                {/* All departments */}
                <div
                  className="flex items-center gap-2.5 px-2 my-1 py-1.5 cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleDeptToggle(p, "all")}
                >
                  <Checkbox
                    size="small"
                    color="secondary"
                    sx={{ p: 0.5 }}
                    checked={sel?.departmentIds === "all"}
                    onChange={() => handleDeptToggle(p, "all")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex items-center gap-2">
                    <img
                      src={SVG.saveAllIcon}
                      alt=""
                      className="w-[18px] h-[18px] object-contain"
                    />
                    <span className="text-base text-gray-700">כל המחלקות</span>
                  </div>
                </div>
                {/* Individual departments */}
                {p.departments.map((dept) => {
                  const isAll = sel?.departmentIds === "all";
                  const isChecked =
                    isAll ||
                    (Array.isArray(sel?.departmentIds) &&
                      sel!.departmentIds.includes(dept.id));
                  const deptIcon = ICONS.find((i) => i.id === dept.icon)?.icon;
                  return (
                    <div
                      key={dept.id}
                      className={`flex items-center gap-2.5 px-2 py-1.5 cursor-pointer my-1 rounded-lg select-none ${isChecked || isAll ? "bg-gray90 hover:bg-gray-200" : "hover:bg-gray-50"}`}
                      onClick={() => handleDeptToggle(p, dept.id)}
                    >
                      <Checkbox
                        size="small"
                        color="secondary"
                        sx={{ p: 0.5 }}
                        checked={isChecked}
                        // disabled={isAll}
                        onChange={() => handleDeptToggle(p, dept.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex items-center gap-1.5">
                        {deptIcon && (
                          <img
                            src={deptIcon}
                            alt=""
                            className="w-[18px] h-[18px] object-contain"
                          />
                        )}
                        <span className={`text-base`}>
                          {dept.name || "מחלקה ללא שם"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── NotifyDeptTreeSelect — trigger input + Popover ───────────────────────────
type NotifyDeptTreeSelectProps = {
  label: string;
  clients: ClientOption[];
  value: NotifyPlaceSelection[];
  onChange: (val: NotifyPlaceSelection[]) => void;
  error?: boolean;
};

export default function NotifyDeptTreeSelect({label, clients, value, onChange, error}: NotifyDeptTreeSelectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
const popoverRef = useRef<{ updatePosition: () => void }>(null as any);

  return (
    <>
      {/* Trigger — MUI filled-style input */}
      <div
        ref={containerRef}
        className={`relative w-full cursor-pointer bg-[#1D1B2014] rounded-t-[4px] transition-colors ${open ? "border-b-2 border-tealBlue" : "border-b border-[rgba(0,0,0,0.42)] hover:border-black"} ${error ? "!border-raspberryRed" : ""}`}
        onClick={() => setAnchorEl(containerRef.current)}
      >
        <div
          className={`flex items-center gap-2 min-h-[40px] px-3 ${value.length > 0 ? "pt-4 pb-1" : "py-3"}`}
        >
          {value.length > 0 ? (
            <>
              <span
                className={`absolute top-1 right-3 text-xs pointer-events-none ${open ? "text-tealBlue" : "text-[rgba(0,0,0,0.6)]"} ${error ? "text-raspberryRed" : ""}`}
              >
                <span className="text-sm text-raspberryRed">*</span>
                {label}
              </span>
              <div
                className="flex flex-wrap gap-1 flex-1"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(containerRef.current);
                }}
              >
                {value.map((sel) => {
                  const k = `${sel.clientId}:${sel.branchId ?? ""}:${sel.subBranchId ?? ""}`;
                  return (
                    <Chip
                      key={k}
                      size="small"
                      label={getNotifChipLabel(sel, clients)}
                      onDelete={() =>
                        onChange(
                          value.filter(
                            (v) =>
                              `${v.clientId}:${v.branchId ?? ""}:${v.subBranchId ?? ""}` !==
                              k,
                          ),
                        )
                      }
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <span
              className={`text-[rgba(0,0,0,0.42)] text-sm flex-1 ${error ? "text-raspberryRed" : ""}`}
            >
              <span className="text-sm text-raspberryRed">*</span>
              {label}
            </span>
          )}
          <img
            src={SVG.dropdownGrayIcon}
            alt=""
            className={`w-2.5 h-2 object-contain shrink-0 ${open ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      <Popover
        action={popoverRef}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              width: containerRef.current?.offsetWidth ?? "auto",
              maxHeight: 300,
              minHeight: 100,
              overflow: "auto",
            },
          },
        }}
      >
        {clients.length === 0 && (
          <div className="p-4 text-center text-sm text-gray-500 flex items-center justify-center">
            עליך להוסיף לקוח/סניף כדי לבחור מחלקות.
          </div>
        )}
        <NotifyDeptList
          clients={clients}
          value={value}
          onChange={onChange}
          popoverRef={popoverRef}
        />
      </Popover>
    </>
  );
}
